import React, { useState, useEffect, useCallback } from "react";
import { Box, Typography } from "@mui/material";

import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import PageContainer from "../layouts/PageContainer";
import AppButton from "../components/buttons/AppButton";
import RoleCard from "../components/roles/RoleCard";
import CreateRoleModal from "../components/roles/CreateRoleModal";
import UpdateRoleModal from "../components/roles/UpdateRoleModal";
import ConfirmDialog from "../components/common/ConfirmDialog";
import AppCircularProgress from "../components/common/AppCircularProgress";

import { usePermissions } from "../hooks/usePermissions";
import { useAppDispatch, useAppSelector } from "../redux/store";
import { getRoles, deleteRole } from "../redux/actions/roleActions";
import { roleActions } from "../redux/slices/role";
import PageError from "../components/common/PageError";
import { RoleResponseDto } from "../shared/dtos/role/RoleResponseDto";

const RolesAndPermissionsPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { hasPermission } = usePermissions();

  // --- SELECTORS ---
  const {
    items: roles,
    loadingFetchAll: loadingRoles,
    errorFetchAll: errorLoadingRoles,
    serverStatus,
    loadingDelete: loadingRoleDelete,
    loadingUpdate: loadingRoleUpdate,
    loadingCreate: loadingRoleCreate,
    errorCreate: errorCreatingRole,
    errorUpdate: errorUpdatingRole,
    // errorDelete: errorDeletingRole
  } = useAppSelector((state) => state.role);

  // --- LOCAL STATE ---
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState<RoleResponseDto | null>(
    null,
  );

  // --- PERMISSIONS ---
  const canCreateRole = hasPermission("role:create");
  const canUpdateRole = hasPermission("role:update");
  const canDeleteRole = hasPermission("role:delete");

  // --- DATA FETCHING ---
  const fetchRoles = useCallback((): void => {
    dispatch(getRoles());
  }, [dispatch]);

  useEffect(() => {
    fetchRoles();
  }, [fetchRoles]);

  // --- MODAL HANDLERS ---
  const handleOpenCreate = (): void => setIsCreateModalOpen(true);

  const handleCloseCreate = useCallback((): void => {
    if (!loadingRoleCreate) setIsCreateModalOpen(false);
  }, [loadingRoleCreate]);

  const handleOpenUpdate = (role: RoleResponseDto): void => {
    setSelectedRole(role);
    setIsUpdateModalOpen(true);
  };

  const handleCloseUpdate = useCallback((): void => {
    if (!loadingRoleUpdate) {
      setIsUpdateModalOpen(false);
      setSelectedRole(null);
    }
  }, [loadingRoleUpdate]);

  const handleOpenDelete = (role: RoleResponseDto): void => {
    setSelectedRole(role);
    setIsDeleteModalOpen(true);
  };

  const handleCloseDelete = useCallback((): void => {
    if (!loadingRoleDelete) {
      setIsDeleteModalOpen(false);
      setSelectedRole(null);
    }
  }, [loadingRoleDelete]);

  const handleDeleteConfirm = (): void => {
    if (selectedRole) {
      dispatch(deleteRole(selectedRole.id));
    }
  };

  // Auto-close modals on success
  useEffect(() => {
    if (!serverStatus) return;

    const { operation, status } = serverStatus;

    if (operation === "create" && status === 201) {
      handleCloseCreate();
    }

    if (operation === "update" && status === 200) {
      handleCloseUpdate();
    }

    if (operation === "delete" && status === 200) {
      handleCloseDelete();
    }

    dispatch(roleActions.setServerStatus(null));
  }, [
    serverStatus,
    dispatch,
    handleCloseCreate,
    handleCloseUpdate,
    handleCloseDelete,
  ]);

  // --- RENDER LOGIC ---
  const renderContent = (): React.ReactNode => {
    if (loadingRoles) {
      return <AppCircularProgress />;
    }

    if (errorLoadingRoles) {
      return <PageError error={errorLoadingRoles} onRetry={fetchRoles} />;
    }

    if (!roles || roles.length === 0) {
      return (
        <Box sx={{ textAlign: "center", mt: 5 }}>
          <Typography>No Roles Found</Typography>
        </Box>
      );
    }

    return (
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2, 1fr)",
            md: "repeat(3, 1fr)",
            lg: "repeat(4, 1fr)",
          },
          gap: 3,
        }}
      >
        {roles.map((role) => (
          <RoleCard
            key={role.id}
            role={role}
            onEdit={canUpdateRole ? () => handleOpenUpdate(role) : undefined}
            onDelete={canDeleteRole ? () => handleOpenDelete(role) : undefined}
          />
        ))}
      </Box>
    );
  };

  return (
    <PageContainer
      title="Roles & Permissions"
      subtitle="Define user roles and assign specific permissions for system access."
      actions={
        canCreateRole && (
          <AppButton
            variant="contained"
            startIcon={<AddCircleOutlineIcon />}
            onClick={handleOpenCreate}
          >
            Create New Role
          </AppButton>
        )
      }
    >
      {renderContent()}

      {/* MODALS */}
      {isCreateModalOpen && (
        <CreateRoleModal
          open={isCreateModalOpen}
          onClose={handleCloseCreate}
          loading={loadingRoleCreate}
          error={errorCreatingRole}
        />
      )}

      {isUpdateModalOpen && selectedRole && (
        <UpdateRoleModal
          open={isUpdateModalOpen}
          onClose={handleCloseUpdate}
          role={selectedRole}
          loading={loadingRoleUpdate}
          error={errorUpdatingRole}
        />
      )}

      {isDeleteModalOpen && selectedRole && (
        <ConfirmDialog
          open={isDeleteModalOpen}
          onClose={handleCloseDelete}
          onConfirm={handleDeleteConfirm}
          title="Delete Role"
          description={`Are you sure you want to delete the role "${selectedRole.name}"? This action cannot be undone.`}
        />
      )}
    </PageContainer>
  );
};

export default RolesAndPermissionsPage;
